import { Link, useRouterState } from '@tanstack/react-router';

interface ProjectViewToggleProps {
  projectId: string;
}

/**
 * Segmented control for switching between the Grid editor and the Timeline
 * view on a project. ENG-720 hoists it into the page header's actions slot via
 * each route's `staticData.headerActions`, so — like
 * `ProductionPlannerViewToggle` — it derives the active view from the router
 * rather than taking it as a prop. That keeps the per-route wiring to a single
 * shared resolver with nothing to get out of sync.
 *
 * The legacy `/projects/$projectId/gantt` route still exists for direct links
 * but is intentionally not part of this toggle, and doesn't declare it.
 */
export function ProjectViewToggle({ projectId }: ProjectViewToggleProps) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const activeView = pathname.endsWith('/timeline') ? 'timeline' : 'grid';

  return (
    <nav
      aria-label="Project planning view"
      className="bg-muted inline-flex items-center gap-0.5 rounded-md p-0.5"
    >
      <Link
        to="/projects/$projectId"
        params={{ projectId }}
        className={`rounded-sm px-3 py-1 text-sm transition-colors ${
          activeView === 'grid'
            ? 'bg-background text-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground'
        }`}
        aria-current={activeView === 'grid' ? 'page' : undefined}
      >
        Grid
      </Link>
      <Link
        to="/projects/$projectId/timeline"
        params={{ projectId }}
        className={`rounded-sm px-3 py-1 text-sm transition-colors ${
          activeView === 'timeline'
            ? 'bg-background text-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground'
        }`}
        aria-current={activeView === 'timeline' ? 'page' : undefined}
      >
        Timeline
      </Link>
    </nav>
  );
}
